import { useAppSelector } from 'hooks/redux';
import React, { createContext, Suspense, useContext } from 'react';
import { useMatch } from 'react-router-dom';

import { authSelectors } from 'app-components/flux/auth';

import AuthDialog from 'components/Dialogs/AuthDialog';
import ErrorBoundary from 'components/ErrorBoundary';
import LayoutDatasetEntitled from 'containers/LayoutDatasetEntitled';
import LayoutDatasets from 'containers/LayoutDatasets';
import UnauthenticatedHomePage from 'containers/UnauthenticatedHomePage/loadable';
import retryPromise from 'utils/retryPromise';

const NoSubscriptionPage = React.lazy(() => retryPromise(() => import('containers/NoSubscriptionPage')));

/** True once the router holds the main app's routes and the user is signed in with an entitled dataset. */
export const EntitledRoutesContext = createContext(false);

/** Signed-out users get the sign-in home on every path below this route. */
export const SignedInGate = (): React.JSX.Element => {
  const isAuthTokenValid = useAppSelector((state) => Boolean(authSelectors.isAuthTokenValid(state)));
  return isAuthTokenValid ? <LayoutDatasets /> : <UnauthenticatedHomePage />;
};

// Without an entitled dataset only these pages render, matched by path.
const NotEntitledPage = (): React.JSX.Element => {
  const isHome = useMatch('/') !== null;
  const isNoSubscription = useMatch('/no-subscription') !== null;

  if (isHome) return <UnauthenticatedHomePage />;
  if (isNoSubscription) {
    return (
      <Suspense fallback={null}>
        <NoSubscriptionPage />
      </Suspense>
    );
  }
  return (
    <ErrorBoundary>
      <AuthDialog />
    </ErrorBoundary>
  );
};

export const EntitledGate = (): React.JSX.Element =>
  useContext(EntitledRoutesContext) ? <LayoutDatasetEntitled /> : <NotEntitledPage />;

// Renders in place of EntitledGate while the main app's routes load, so the layouts above stay mounted.
export const EntitledRoutesFallback = (): null => null;
